import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from 'primeng/colorpicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FORM_THEME_PRESETS,
  FORM_THEME_RTL_OPTIONS,
  FormTheme
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-theme-panel',
  imports: [
    FormsModule,
    SelectModule,
    InputTextModule,
    ColorPickerModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  template: `
    <section class="theme-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.theme.infoTitle'"
        [descriptionKey]="'dynamic.builder.theme.infoDescription'"
        [bulletKeys]="[
          'dynamic.builder.theme.infoBulletPreview',
          'dynamic.builder.theme.infoBulletLogo'
        ]" />

      <ain-form-field [label]="t('dynamic.builder.theme.preset')">
        <p-select
          [options]="presetOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="theme().preset ?? 'default'"
          [disabled]="!canEdit()"
          appendTo="body"
          (ngModelChange)="patch({ preset: $event })" />
      </ain-form-field>

      <ain-form-field [label]="t('dynamic.builder.theme.primaryColor')">
        <div class="color-row">
          <p-colorPicker
            [ngModel]="primaryColor()"
            [disabled]="!canEdit()"
            format="hex"
            (ngModelChange)="patch({ primaryColor: $event || undefined })" />
          <span
            class="color-swatch"
            [style.background]="primaryColor() || '#0f766e'"
            [attr.aria-label]="t('dynamic.builder.theme.primaryColor')"></span>
          <input
            pInputText
            class="color-hex"
            [ngModel]="primaryColor()"
            [disabled]="!canEdit()"
            [placeholder]="'#0f766e'"
            (ngModelChange)="patch({ primaryColor: normalizeHex($event) })" />
        </div>
      </ain-form-field>

      <ain-form-field [label]="t('dynamic.builder.theme.logoFileId')">
        <input
          pInputText
          [ngModel]="theme().logoFileId ?? ''"
          [disabled]="!canEdit()"
          [placeholder]="t('dynamic.builder.theme.logoPlaceholder')"
          (ngModelChange)="patch({ logoFileId: $event.trim() || undefined })" />
        <small class="hint">{{ t('dynamic.builder.theme.logoHint') }}</small>
      </ain-form-field>

      <ain-form-field [label]="t('dynamic.builder.theme.rtl')">
        <p-select
          [options]="rtlOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="theme().rtl ?? 'auto'"
          [disabled]="!canEdit()"
          appendTo="body"
          (ngModelChange)="patch({ rtl: $event })" />
      </ain-form-field>
    </section>
  `,
  styles: [`
    .theme-panel { display: grid; gap: 12px; padding: 4px 0; max-width: 520px; }
    .color-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .color-swatch {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      border: 1px solid #cbd5e1;
    }
    .color-hex { flex: 1; min-width: 120px; }
    .hint { color: var(--text-color-secondary); }
  `]
})
export class FormBuilderThemePanelComponent {
  private readonly localization = inject(LocalizationService);

  readonly theme = input<FormTheme>({ preset: 'default', rtl: 'auto' });
  readonly canEdit = input(true);

  readonly themeChange = output<FormTheme>();

  readonly presetOptions = computed(() =>
    FORM_THEME_PRESETS.map(item => ({ label: this.t(item.labelKey), value: item.value }))
  );

  readonly rtlOptions = computed(() =>
    FORM_THEME_RTL_OPTIONS.map(item => ({ label: this.t(item.labelKey), value: item.value }))
  );

  readonly primaryColor = computed(() => this.theme().primaryColor ?? '#0f766e');

  patch(partial: Partial<FormTheme>): void {
    this.themeChange.emit({ ...this.theme(), ...partial });
  }

  normalizeHex(value: string): string | undefined {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
